import { Controller } from "@hotwired/stimulus"

/**
 * IoT Sensor Config Wizard Controller
 * 5-step wizard for sensor bulk configuration.
 *
 * Navigation, step indicators (mobile + desktop), per-step validation,
 * and a JS-rendered review summary on step 5. Form is submitted normally
 * (no fetch/AJAX) so Rails CSRF handling applies automatically.
 */
export default class extends Controller {
  static targets = [
    "stepPanel",
    "stepDot",
    "stepDotContent",
    "stepLabel",
    "stepConnector",
    "stepIndicator",
    "mobileStepNum",
    "mobileStepTitle",
    "mobileProgressBar",
    "previousButton",
    "nextButton",
    "applyButton",
    "reviewSummary",
    "stepError",
  ]

  static values = {
    currentStep: { type: Number, default: 0 },
    totalSteps: { type: Number, default: 5 },
    stepTitles: { type: Array, default: [] },
    // The review step is built here, in the browser, so its labels have to
    // arrive already translated. `labels` is keyed "<section>.<field>" and
    // reuses the exact i18n keys the inputs use; `optionLabels` maps a wire
    // token ("immediate") to the label the operator actually picked.
    // Before CONEK-5757 every one of these was a hardcoded English literal.
    labels: { type: Object, default: {} },
    optionLabels: { type: Object, default: {} },
  }

  label(key, fallback) {
    return this.labelsValue[key] || fallback
  }

  optionLabel(token) {
    return this.optionLabelsValue[token] || token
  }

  connect() {
    this.showStep(this.currentStepValue)
  }

  next() {
    this.clearStepError(this.currentStepValue)

    // The current panel is already on screen, so focusing here is safe.
    if (!this.validateStep(this.currentStepValue)) {
      this.focusFirstInvalid(this.currentStepValue)
      return
    }

    if (this.currentStepValue < this.totalStepsValue - 1) {
      this.currentStepValue++
      this.showStep(this.currentStepValue)

      if (this.currentStepValue === this.totalStepsValue - 1) {
        this.renderReview()
      }
    }
  }

  previous() {
    if (this.currentStepValue > 0) {
      this.clearStepError(this.currentStepValue)
      this.currentStepValue--
      this.showStep(this.currentStepValue)
    }
  }

  // Direct jump when the operator clicks on a step in the indicator.
  // Backwards jumps skip validation (letting them fix an earlier step).
  // Forward jumps still run validateStep on the current one so a
  // half-filled required field can't be bypassed.
  goToStep(event) {
    const target = parseInt(event.currentTarget.dataset.step, 10)
    if (Number.isNaN(target) || target === this.currentStepValue) return

    if (target > this.currentStepValue) {
      this.clearStepError(this.currentStepValue)
      if (!this.validateStep(this.currentStepValue)) {
        this.focusFirstInvalid(this.currentStepValue)
        return
      }
    }

    this.clearStepError(this.currentStepValue)
    this.currentStepValue = target
    this.showStep(this.currentStepValue)

    if (this.currentStepValue === this.totalStepsValue - 1) {
      this.renderReview()
    }
  }

  showStep(index) {
    const current = index
    const total = this.totalStepsValue

    // Show/hide step panels
    this.stepPanelTargets.forEach((panel) => {
      const stepIndex = parseInt(panel.dataset.step, 10)
      panel.classList.toggle("hidden", stepIndex !== current)
    })

    // Step dot styling
    this.stepDotTargets.forEach((dot) => {
      const stepIndex = parseInt(dot.dataset.step, 10)
      dot.classList.remove(
        "bg-primary",
        "border-primary",
        "text-primary-foreground",
        "bg-border",
        "border-border",
        "text-muted-foreground",
        "bg-green-500",
        "border-green-500",
        "text-white",
      )
      if (stepIndex < current) {
        dot.classList.add("bg-green-500", "border-green-500", "text-white")
      } else if (stepIndex === current) {
        dot.classList.add(
          "bg-primary",
          "border-primary",
          "text-primary-foreground",
        )
      } else {
        dot.classList.add("bg-border", "border-border", "text-muted-foreground")
      }
    })

    // Step dot content (checkmark for completed, number for others)
    this.stepDotContentTargets.forEach((content) => {
      const stepIndex = parseInt(content.dataset.step, 10)
      if (stepIndex < current) {
        content.innerHTML = `<svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>`
      } else {
        content.textContent = stepIndex + 1
      }
    })

    // Step label styling
    this.stepLabelTargets.forEach((label) => {
      const stepIndex = parseInt(label.dataset.step, 10)
      label.classList.remove(
        "text-primary",
        "font-medium",
        "text-muted-foreground",
        "text-foreground",
      )
      if (stepIndex === current) {
        label.classList.add("text-primary", "font-medium")
      } else if (stepIndex < current) {
        label.classList.add("text-foreground")
      } else {
        label.classList.add("text-muted-foreground")
      }
    })

    // Connector bar colours
    this.stepConnectorTargets.forEach((connector) => {
      const stepIndex = parseInt(connector.dataset.step, 10)
      connector.classList.toggle("bg-green-500", stepIndex < current)
      connector.classList.toggle("bg-border", stepIndex >= current)
    })

    // Previous button: invisible on step 0
    if (this.hasPreviousButtonTarget) {
      this.previousButtonTarget.classList.toggle("invisible", current === 0)
    }

    // Next button: hidden on last step
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", current === total - 1)
    }

    // Apply button: only on the last step. `disabled` as well as `hidden` —
    // a CSS-hidden submit button is still the form's default button, so Enter
    // in any number field used to submit the wizard from any step, skipping
    // validateStep entirely (CONEK-5757).
    if (this.hasApplyButtonTarget) {
      const onReview = current === total - 1
      this.applyButtonTarget.classList.toggle("hidden", !onReview)
      this.applyButtonTarget.disabled = !onReview
      // Ui::ButtonComponent renders aria-disabled from its server-side
      // `disabled:`, so flipping only the DOM property leaves the button
      // announcing itself as disabled to assistive tech on the review step —
      // and Playwright, which reads the same attribute, refuses to click it.
      this.applyButtonTarget.setAttribute("aria-disabled", String(!onReview))
    }

    // Mobile compact indicator
    if (this.hasMobileStepNumTarget) {
      this.mobileStepNumTarget.textContent = String(current + 1)
    }
    if (this.hasMobileStepTitleTarget && this.stepTitlesValue.length) {
      this.mobileStepTitleTarget.textContent =
        this.stepTitlesValue[current] || ""
    }
    if (this.hasMobileProgressBarTarget && total) {
      const pct = Math.round(((current + 1) / total) * 100)
      this.mobileProgressBarTarget.style.width = `${pct}%`
    }
  }

  toggleSleepInterval(event) {
    const field = this.element.querySelector("#wizard-sleep-interval-field")
    if (field) {
      field.classList.toggle("hidden", !event.target.checked)
    }
  }

  renderReview() {
    if (!this.hasReviewSummaryTarget) return

    const sections = [
      this.buildSensitivitySection(),
      this.buildAccelSection(),
      this.buildTimingSection(),
      this.buildActionsSection(),
    ]

    this.reviewSummaryTarget.innerHTML = sections.join("")
  }

  // --- Private ---

  // Backstop, not the main gate: goToStep already refuses to move forward off an
  // invalid step, so an operator cannot normally reach Review with a bad value
  // behind them. This catches the ways around that — a re-enabled button, a key
  // press, a future change that loosens the forward gate — and it is what makes
  // `novalidate` safe: with native validation off, nothing else stops a submit.
  // Checking EVERY step matters because the browser cannot focus a display:none
  // control, which is how the old silent no-op happened in the first place.
  validateBeforeSubmit(event) {
    for (let step = 0; step < this.totalStepsValue; step++) {
      if (this.validateStep(step)) continue

      event.preventDefault()
      // Reveal the panel BEFORE focusing: focus() is a no-op on a display:none
      // element, so focusing from inside validateStep would silently do nothing
      // for every step that is not the visible one.
      this.showStep(step)
      this.focusFirstInvalid(step)
      return false
    }
    return true
  }

  // Reports on the step but never focuses — the caller owns that, because only
  // the caller knows whether the panel is on screen yet.
  validateStep(step) {
    const invalid = this.firstInvalidInput(step)
    if (!invalid) {
      this.clearStepError(step)
      return true
    }

    const message =
      invalid.value.trim() === ""
        ? this.label(
            "required_error",
            "Fill in every required field before continuing.",
          )
        : // Browser-localised, and specific about which constraint failed.
          invalid.validationMessage
    this.showStepError(step, message)
    return false
  }

  // checkValidity() covers required AND min/max/step, so the range the input
  // advertises is the range the operator is held to — previously only emptiness
  // was checked and everything else fell through to a submit the browser then
  // blocked without saying so.
  firstInvalidInput(step) {
    const panel = this.stepPanelTargets.find(
      (p) => parseInt(p.dataset.step, 10) === step,
    )
    if (!panel) return null

    return (
      Array.from(panel.querySelectorAll("input[type='number']")).find(
        (input) =>
          !this.inHiddenWrapper(input, panel) && !input.checkValidity(),
      ) || null
    )
  }

  // A control the operator cannot see must not block the step: they would have
  // nothing to fix and focus() cannot even reach it. Walk up to the panel and
  // stop there — the panel's OWN hidden state is not a reason to skip, or
  // validateBeforeSubmit could never check any step but the visible one.
  inHiddenWrapper(input, panel) {
    for (
      let el = input.parentElement;
      el && el !== panel;
      el = el.parentElement
    ) {
      if (el.classList.contains("hidden")) return true
    }
    return false
  }

  focusFirstInvalid(step) {
    this.firstInvalidInput(step)?.focus()
  }

  showStepError(step, message) {
    const errorEl = this.stepErrorTargets.find(
      (el) => parseInt(el.dataset.step, 10) === step,
    )
    if (errorEl) {
      errorEl.textContent = message
      errorEl.classList.remove("hidden")
    }
  }

  clearStepError(step) {
    const errorEl = this.stepErrorTargets.find(
      (el) => parseInt(el.dataset.step, 10) === step,
    )
    if (errorEl) {
      errorEl.textContent = ""
      errorEl.classList.add("hidden")
    }
  }

  // Escape user-controlled input values before they land inside
  // renderReview()'s innerHTML. A pasted `<img onerror=…>` in a number
  // field would otherwise execute when the operator hits step 5.
  escapeHTML(value) {
    const div = document.createElement("div")
    div.textContent = String(value)
    return div.innerHTML
  }

  readInput(name) {
    const el = this.element.querySelector(`[name="${name}"]`)
    return el ? this.escapeHTML(el.value || "—") : "—"
  }

  readChecked(name) {
    const el = this.element.querySelector(`[name="${name}"]:checked`)
    return el ? this.escapeHTML(this.optionLabel(el.value)) : "—"
  }

  readCheckboxes(name) {
    const els = this.element.querySelectorAll(`[name="${name}"]:checked`)
    // The blank hidden companion that keeps the key alive when everything is
    // unchecked is not a selection — never show it as one.
    const values = Array.from(els)
      .map((el) => el.value)
      .filter((value) => value !== "")
    if (values.length === 0) return "—"
    return values
      .map((value) => this.escapeHTML(this.optionLabel(value)))
      .join(", ")
  }

  reviewSectionHTML(stepNum, title, rows) {
    const rowsHTML = rows
      .map(
        ([label, value]) => `
        <div>
          <dt class="text-muted-foreground text-xs">${label}</dt>
          <dd class="text-foreground text-sm mt-0.5">${value}</dd>
        </div>`,
      )
      .join("")

    return `
      <div class="rounded-md border border-border bg-muted/30 p-4">
        <h4 class="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <span class="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">${stepNum}</span>
          ${title}
        </h4>
        <dl class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          ${rowsHTML}
        </dl>
      </div>`
  }

  buildSensitivitySection() {
    const rows = [
      [
        this.label("sensitivity.warning_percent", "Warning %"),
        this.readInput("sensor_node[sensitivity][warning_percent]"),
      ],
      [
        this.label("sensitivity.alert_percent", "Alert %"),
        this.readInput("sensor_node[sensitivity][alert_percent]"),
      ],
    ]

    // CONEK-7005: the advisory field is rendered only for a legacy RackEye
    // (its profile has an advisory tier), so the review row follows the input
    // rather than appearing blank for every SmartCap.
    const advisoryName = "sensor_node[sensitivity][advisory_percent]"
    if (this.element.querySelector(`[name="${advisoryName}"]`)) {
      rows.push([
        this.label("sensitivity.advisory_percent", "Advisory %"),
        this.readInput(advisoryName),
      ])
    }

    return this.reviewSectionHTML(
      1,
      this.stepTitlesValue[0] || "Sensitivity",
      rows,
    )
  }

  buildAccelSection() {
    return this.reviewSectionHTML(
      2,
      this.stepTitlesValue[1] || "Accelerometer",
      [
        "x_threshold",
        "y_threshold",
        "z_threshold",
        "x_duration",
        "y_duration",
        "z_duration",
        "sample_rate",
        "sample_count",
        "pre_trigger_samples",
      ].map((field) => [
        this.label(`accel.${field}`, field),
        this.readInput(`sensor_node[accel][${field}]`),
      ]),
    )
  }

  buildTimingSection() {
    const sleepEnabled = this.element.querySelector(
      "[name='sensor_node[timing][sleep_mode_enabled]']:checked",
    )
    const sleepRow = sleepEnabled
      ? [
          [
            this.label("timing.sleep_interval_seconds", "Sleep interval (s)"),
            this.readInput("sensor_node[timing][sleep_interval_seconds]"),
          ],
        ]
      : []

    return this.reviewSectionHTML(3, this.stepTitlesValue[2] || "Timing", [
      ...[
        "ping_interval_seconds",
        "event_report_interval_seconds",
        "alive_check_interval_seconds",
      ].map((field) => [
        this.label(`timing.${field}`, field),
        this.readInput(`sensor_node[timing][${field}]`),
      ]),
      [
        this.label("timing.sleep_mode_enabled", "Sleep mode"),
        sleepEnabled
          ? this.label("enabled", "Enabled")
          : this.label("disabled", "Disabled"),
      ],
      ...sleepRow,
    ])
  }

  buildActionsSection() {
    return this.reviewSectionHTML(4, this.stepTitlesValue[3] || "Actions", [
      [
        this.label("action.response_type", "Response type"),
        this.readChecked("sensor_node[action][response_type]"),
      ],
      [
        this.label("action.enabled_codes", "Enabled codes"),
        this.readCheckboxes("sensor_node[action][enabled_codes][]"),
      ],
    ])
  }
}
